import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowsClockwise, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { usePortalIdentity, type TenantRole } from "../auth/usePortalIdentity";
import { useSession } from "../auth/SessionProvider";
import { useMfaGate } from "../auth/mfa";
import {
  clearUnverifiedFactors,
  confirmEnrollment,
  ENROLLMENT_FAILURE_TEXT,
  removeFactor,
  startEnrollment,
  verifiedFactorId,
  verifiedFactorKey,
  type EnrollmentStart
} from "../auth/mfaEnrollment";
import { REAUTH_FAILURE_TEXT, verifyCurrentPassword } from "../auth/reauthenticate";
import { recordSecurityEvent } from "../auth/securityEvents";
import { DevicePanel } from "./DevicePanel";
import { ManagementPanel } from "./ManagementPanel";
import { MembershipPanel } from "./MembershipPanel";
import { NotificationPreferencesPanel } from "./NotificationPreferencesPanel";
import { SessionsPanel } from "./SessionsPanel";
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_HINT,
  weakPasswordMessage
} from "../auth/passwordRules";
import { useTurnstile } from "../auth/useTurnstile";
import { useUpdateOwnProfile } from "../data/accountQueries";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure } from "../lib/errors";
import { BoxState } from "../ui/BoxState";
import { Quiet } from "../ui/Badge";
import { ClientHead } from "../ui/ClientHead";
import { Hint } from "../ui/Hint";
import { Panel } from "../ui/Panel";
import { RowMenu } from "../ui/RowMenu";
import { Sheet } from "../ui/Sheet";
import { AppearancePanel } from "./clientSettings/AppearancePanel";
import { SettingsNav } from "./clientSettings/SettingsNav";
import { LEGACY_ANCHORS, SECTIONS, sectionFor, type SettingsSectionKey } from "./clientSettings/sections";
import "./clientSettings/settings.css";
import { englishPathFor, SETTINGS_SEGMENT } from "../lib/englishPaths";
import { useClientPath, useViewOnly } from "../data/readSource";
import { PersonalLeftOut } from "./asClient/PersonalLeftOut";

/**
 * Inställningar: the person's own account, and the company's förvaltning and people.
 *
 * **Two groups and an address per section, since the second build round of 2026-09-24.** Until then
 * this was one long page of eight panels under the heading "Ditt konto", with the company's people
 * and its förvaltning among them. The sketch Mattej approved that day (`prototype/kund/pages.js`,
 * `settingsPage`) split it the way design.md section 3 splits "du" and "ni", and each section became
 * `/settings/<del>`. On a phone `/settings` is the list and a section opens alone; on a computer the
 * list stays on the left and `/settings` shows the first section beside it. An address that names no
 * section goes back to the list rather than to a not-found page, because a mistyped section is still
 * a settings address and the list is the useful answer to it.
 *
 * **Three of the panels are also Petrovic's.** `AdminSettingsRoute.tsx` renders `ProfilePanel`,
 * `PasswordPanel` and `FactorPanel` from here, `SessionsPanel` and `DevicePanel` from their own files,
 * for his own account. Each panel draws its own box since this round, so both pages get one box per
 * panel and neither wraps a second box around it.
 *
 * What is deliberately not here, and why it is not a shortcut:
 *
 * - **Losing the authenticator entirely cannot be self-served from this screen.** A session at
 *   `aal1` holding a verified factor is sent to `/verify` and stays there, so somebody who has
 *   lost the factor never reaches settings at all. Recovering from that needs proof arriving through
 *   a channel this device does not control. Naming it is the honest answer; a "remove my factor"
 *   button reachable without the factor would be the second factor removing itself.
 *
 *   That sentence was written before it was true. Until 2026-09-08 `RequireSession` asked
 *   `status === "signed-in"` and nothing else, so an `aal1` session holding a verified factor
 *   reached this screen by typing the path. It reads the same gate now, and
 *   `public.current_tenant_ids()` is empty for that session besides.
 *
 * `ManagementPanel` sits under the company's group because plan.md section 5.12 puts the end of
 * management under `Settings > Management`. The factor section is not a step a person has to take
 * first: since migration `20260905160001_phase5e_step_up_wraps_the_actions_that_already_consume_it.sql`
 * a client with no verified factor proves a cancellation with the current password instead, which is
 * plan.md 14.3 as written and plan.md section 4, "Optional TOTP MFA for client users", keeping a
 * client's TOTP optional.
 */

/** The company's two sections; every other one is the person's own, left out of "Visa som kund". */
const COMPANY_SECTIONS: readonly SettingsSectionKey[] = ["forvaltning", "personer"];

function Section({ section }: { section: SettingsSectionKey }) {
  const viewOnly = useViewOnly();
  if (viewOnly && !COMPANY_SECTIONS.includes(section)) {
    return <PersonalLeftOut />;
  }
  switch (section) {
    case "konto":
      return <ProfilePanel />;
    case "sakerhet":
      return (
        <>
          <PasswordPanel />
          <FactorPanel />
        </>
      );
    case "inloggningar":
      return <SessionsPanel />;
    case "notiser":
      return (
        <>
          <DevicePanel />
          <NotificationPreferencesPanel />
        </>
      );
    case "utseende":
      return <AppearancePanel />;
    case "forvaltning":
      return <ManagementPanel />;
    case "personer":
      return <MembershipPanel />;
  }
}

export function SettingsRoute() {
  const { section } = useParams<{ section?: string }>();
  const location = useLocation();
  const identity = usePortalIdentity();
  const clientPath = useClientPath();

  const current = sectionFor(section);
  const tenantName = identity.data?.tenantId
    ? (identity.data.tenantDisplayName ?? "Ert företag")
    : null;

  if (!section) {
    const moved = LEGACY_ANCHORS[location.hash.replace(/^#/, "")];
    if (moved) {
      return <Navigate to={clientPath(`/settings/${SETTINGS_SEGMENT[moved]}`)} replace />;
    }
  } else if (!current) {
    // An old Swedish section (`/settings/konto`) goes to its English address; anything else to the list.
    const english = englishPathFor(location.pathname);
    return <Navigate to={english ? `${english}${location.search}${location.hash}` : clientPath("/settings")} replace />;
  }

  /*
    On the list the body is the first section, for the computer's second column; the stylesheet hides
    it on a phone. On a section the body comes first in the document, so its h1 is the first heading
    a screen reader meets, and the stylesheet puts the list back on the left on a computer.
  */
  if (!current) {
    return (
      <div className="k-page is-wide">
        <ClientHead title="Inställningar" titleId="settings-title" />
        <div className="k-settings is-index">
          <div className="k-set-nav">
            <SettingsNav active={null} tenantName={tenantName} />
          </div>
          <div className="k-set-body stack" data-testid="settings-first-section">
            <Section section={SECTIONS[0].key} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page is-wide">
      <div className="k-settings">
        <div className="k-set-body">
          <ClientHead
            title={current.label}
            titleId="settings-title"
            back={{ to: clientPath("/settings"), label: "Inställningar" }}
          />
          <div className="stack">
            <Section section={current.key} />
          </div>
        </div>
        <div className="k-set-nav">
          <p className="k-set-nav-title" aria-hidden="true">
            Inställningar
          </p>
          <SettingsNav active={current.key} tenantName={tenantName} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ profile */

const ROLE_LABEL: Record<TenantRole, string> = {
  client_authorised_contact: "Behörig kontaktperson",
  client_member: "Användare"
};

/** A box that holds only a state: loading, failed or refused. */
function StatePanel({ title, titleId, children }: { title: string; titleId: string; children: ReactNode }) {
  return (
    <Panel title={title} titleId={titleId} flush>
      {children}
    </Panel>
  );
}

/**
 * Namn och inloggning: who the person is in the portal, as facts, with the name editable.
 *
 * **The sign-in address is new here, and approved on 2026-09-24.** Before, the portal never showed a
 * person which address they sign in with. It is read from the session, the same place the password
 * check reads it, and it cannot be changed from here: Petrovic changes it, and the person confirms
 * the change from the new address (`ConfirmEmailChangeRoute`).
 */
export function ProfilePanel() {
  const identity = usePortalIdentity();
  const { session } = useSession();
  const [editing, setEditing] = useState(false);
  const email = session?.user?.email ?? null;

  if (identity.isLoading) {
    return (
      <StatePanel title="Namn och inloggning" titleId="profile-title">
        <BoxState kind="loading" title="Hämtar ditt konto" compact />
      </StatePanel>
    );
  }

  if (identity.isError) {
    return (
      <StatePanel title="Namn och inloggning" titleId="profile-title">
        <BoxState
          kind="error"
          title="Vi kunde inte hämta ditt konto"
          body="Ingenting har ändrats. Försök igen."
          onRetry={() => void identity.refetch()}
          compact
        />
      </StatePanel>
    );
  }

  const data = identity.data;
  const role = data?.role ?? null;

  return (
    <>
      <Panel title="Namn och inloggning" titleId="profile-title" testId="profile-panel">
        <dl className="kv">
          <div>
            <dt>Namn som visas</dt>
            <dd className="k-set-value">
              <span data-testid="profile-display-name">
                {data?.displayName ?? <Quiet>Namn saknas</Quiet>}
              </span>
              <button
                type="button"
                className="button is-quiet"
                aria-label="Ändra namn"
                onClick={() => setEditing(true)}
              >
                Ändra
              </button>
            </dd>
          </div>
          <div>
            <dt>Förnamn</dt>
            <dd>{data?.firstName ?? <Quiet>Saknas</Quiet>}</dd>
          </div>
          <div>
            <dt>Loggar in med</dt>
            <dd className="k-set-value">
              {email ? (
                <span className="mono" data-testid="profile-email">
                  {email}
                </span>
              ) : (
                <Quiet>Går inte att visa just nu</Quiet>
              )}
              <Hint>Adressen byts av Petrovic Systems. Du bekräftar bytet från den nya adressen.</Hint>
            </dd>
          </div>
          {role ? (
            <div>
              <dt>Roll</dt>
              <dd data-testid="profile-role">{ROLE_LABEL[role]}</dd>
            </div>
          ) : null}
        </dl>
      </Panel>

      <Sheet open={editing} onOpenChange={setEditing} title="Ändra namn" testId="profile-sheet">
        <ProfileForm
          initialFirstName={data?.firstName ?? ""}
          initialDisplayName={data?.displayName ?? ""}
        />
      </Sheet>
    </>
  );
}

/**
 * The name form, in the sheet. Seeded from the server once, when the sheet opens: the sheet renders
 * nothing while closed, so every opening starts from what the server holds and a later refetch never
 * overwrites what the person is in the middle of typing.
 */
function ProfileForm({
  initialFirstName,
  initialDisplayName
}: {
  initialFirstName: string;
  initialDisplayName: string;
}) {
  const update = useUpdateOwnProfile();

  const [firstName, setFirstName] = useState(initialFirstName);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [saved, setSaved] = useState(false);

  const blank = firstName.trim().length === 0 || displayName.trim().length === 0;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaved(false);

    try {
      await update.mutateAsync({ firstName, displayName });
      setSaved(true);
    } catch {
      // Rendered from the mutation's own error below.
    }
  }

  return (
    <form className="k-form" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor="first-name">Förnamn</label>
        <input
          id="first-name"
          name="first-name"
          autoComplete="given-name"
          maxLength={120}
          value={firstName}
          disabled={update.isPending}
          onChange={(event) => {
            setFirstName(event.target.value);
            setSaved(false);
          }}
        />
      </div>

      <div className="field">
        <label htmlFor="display-name">Namn som visas</label>
        <input
          id="display-name"
          name="display-name"
          autoComplete="name"
          maxLength={120}
          value={displayName}
          disabled={update.isPending}
          onChange={(event) => {
            setDisplayName(event.target.value);
            setSaved(false);
          }}
        />
        <p className="settings-note">
          Så här visas du för dina kollegor i portalen. Petrovic Systems ser samma namn.
        </p>
      </div>

      {/*
        Said plainly rather than enforced silently. `update_own_profile` treats a null as "leave it
        alone" and the database refuses an empty string, so there is no way through this gateway to
        remove a name once it exists. A form that accepted a cleared field and changed nothing
        would be lying about what it did.
      */}
      <p className="settings-note">Ett namn går att ändra men inte att ta bort.</p>

      {update.isError ? (
        <p className="form-error" role="alert" data-testid="profile-error">
          {toPortalFailure(update.error).message}
        </p>
      ) : null}

      {saved ? (
        <p className="form-success" role="status" data-testid="profile-saved">
          Namnet är sparat.
        </p>
      ) : null}

      <div className="form-actions">
        <button type="submit" className="button is-primary" disabled={blank || update.isPending}>
          <span>{update.isPending ? "Sparar" : "Spara namn"}</span>
        </button>
      </div>
    </form>
  );
}

/* ----------------------------------------------------------------- password */

/**
 * Lösenord: a box with one act, and the form in a sheet (the sketch's `losenord` sheet).
 *
 * The form is not rendered at all rather than rendered and refused when the session has no address
 * or no user id. Without them the check has nothing to check against, and a form that cannot work is
 * worse company than a sentence saying so.
 */
export function PasswordPanel() {
  const { session, userId } = useSession();
  const [open, setOpen] = useState(false);
  const email = session?.user?.email ?? null;

  if (!email || !userId) {
    return (
      <StatePanel title="Lösenord" titleId="password-title">
        <BoxState
          kind="error"
          title="Vi kan inte se vilket konto det gäller"
          body="Ladda om sidan och försök igen."
          compact
        />
      </StatePanel>
    );
  }

  return (
    <>
      <Panel
        title="Lösenord"
        titleId="password-title"
        className="k-set-headonly"
        flush
        actions={
          <button
            type="button"
            className="button"
            data-testid="password-open"
            onClick={() => setOpen(true)}
          >
            Byt lösenord
          </button>
        }
      />

      <Sheet open={open} onOpenChange={setOpen} title="Byt lösenord" testId="password-sheet">
        <PasswordForm email={email} userId={userId} />
      </Sheet>
    </>
  );
}

function PasswordForm({ email, userId }: { email: string; userId: string }) {
  const [turnstile, turnstileMount] = useTurnstile();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [pending, setPending] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready =
    current.length > 0 &&
    next.length >= MIN_PASSWORD_LENGTH &&
    next === repeat &&
    !turnstile.pending;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) {
      return;
    }

    setPending(true);
    setFailure(null);
    setChanged(false);

    try {
      const proof = await verifyCurrentPassword(
        email,
        current,
        userId,
        turnstile.token ?? undefined
      );
      if (!proof.ok) {
        setFailure(REAUTH_FAILURE_TEXT[proof.reason]);
        return;
      }

      const supabase = getSupabaseBrowserClient();
      /**
       * The old password travels with the new one, and that is not a duplicate of the check above.
       *
       * `GOTRUE_SECURITY_UPDATE_PASSWORD_REQUIRE_CURRENT_PASSWORD` is on for this project, and when
       * it is on GoTrue refuses an update that does not carry `current_password`. Sending it makes
       * the server enforce the same thing the check above enforces, rather than the portal's check
       * being the only one. When the setting is off the field is simply the truth and costs nothing.
       *
       * Measured on 2026-09-03: a session minted from a recovery or a magic link may set a password
       * with the new one alone, which is why `SetPasswordRoute` sends no `current_password`. What
       * could not be measured is this path, a session obtained with a password, because staging's
       * captcha refuses `grant_type=password` to anything that is not a browser. Sending the field
       * is what makes that gap not matter.
       */
      const { error } = await supabase.auth.updateUser({
        password: next,
        current_password: current
      });

      if (error) {
        // The strength rules are the project's. `weakPasswordMessage` reads GoTrue's own reasons
        // rather than guessing from the value, so the sentence names what was actually wrong.
        setFailure(weakPasswordMessage(error) ?? "Lösenordet kunde inte bytas. Försök igen.");
        return;
      }

      setCurrent("");
      setNext("");
      setRepeat("");
      setChanged(true);

      // plan.md section 5.7: mandatory in-app and by mail, and it cannot be switched off. GoTrue
      // owns the change and no role here may read its audit table, so this call is the only record
      // that it happened. It never throws and the screen never waits on it: the password did change,
      // and a failure to write the notice is not something to show the person who just changed it.
      void recordSecurityEvent("password_changed");
    } finally {
      // A Turnstile token is single use, so the next attempt needs a fresh one whatever happened.
      turnstile.reset();
      setPending(false);
    }
  }

  return (
    <form className="k-form" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor="current-password">Nuvarande lösenord</label>
        <input
          id="current-password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          disabled={pending}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <p className="settings-note">
          Ange ditt nuvarande lösenord först. Det är så vi vet att det är du och inte någon som råkat
          hitta en olåst dator.
        </p>
      </div>

      <div className="field">
        <label htmlFor="new-password">Nytt lösenord</label>
        <div className="password-field">
          <input
            id="new-password"
            type={revealed ? "text" : "password"}
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={next}
            disabled={pending}
            onChange={(event) => setNext(event.target.value)}
          />
          <button
            type="button"
            className="icon-button password-toggle"
            aria-label={revealed ? "Dölj lösenord" : "Visa lösenord"}
            aria-pressed={revealed}
            onClick={() => setRevealed((value) => !value)}
          >
            {revealed ? (
              <EyeSlash size={18} aria-hidden="true" />
            ) : (
              <Eye size={18} aria-hidden="true" />
            )}
          </button>
        </div>
        <p className="settings-note">{PASSWORD_HINT}</p>
      </div>

      <div className="field">
        <label htmlFor="repeat-password">Upprepa nytt lösenord</label>
        <input
          id="repeat-password"
          type="password"
          autoComplete="new-password"
          required
          value={repeat}
          disabled={pending}
          onChange={(event) => setRepeat(event.target.value)}
        />
      </div>

      {tooShort ? (
        <p className="form-error" role="alert">
          Lösenordet måste vara minst {MIN_PASSWORD_LENGTH} tecken.
        </p>
      ) : null}

      {mismatch ? (
        <p className="form-error" role="alert">
          De två fälten är inte lika.
        </p>
      ) : null}

      {failure ? (
        <p className="form-error" role="alert" data-testid="password-error">
          {failure}
        </p>
      ) : null}

      {changed ? (
        <p className="form-success" role="status" data-testid="password-changed">
          Lösenordet är bytt. Du är fortfarande inloggad på den här enheten.
        </p>
      ) : null}

      {turnstile.sitekey ? (
        <div ref={turnstileMount} className="cf-turnstile" data-testid="turnstile-password" />
      ) : null}

      <div className="form-actions">
        <button type="submit" className="button is-primary" disabled={!ready || pending}>
          <span>
            {pending
              ? "Byter lösenord"
              : turnstile.pending
                ? "Säkerhetskontroll pågår"
                : "Byt lösenord"}
          </span>
        </button>
      </div>
    </form>
  );
}

/* --------------------------------------------------------------- the factor */

// The key lives beside `verifiedFactorId` in `mfaEnrollment.ts`, because the management panel reads
// the same answer to decide whether a step-up can be proved at all.
const factorsKey = verifiedFactorKey;

const FACTOR_HINT =
  "En andra faktor gör att ett stulet lösenord inte räcker för att komma in. Den behövs också för att ta emot en hemlighet från Petrovic Systems.";
const FACTOR_REQUIRED_HINT =
  "Ditt konto kräver en andra faktor. Den går att byta till en ny telefon, men inte att stänga av.";

/**
 * Tvåfaktorsinloggning: the state as a word, the two changes behind the row's menu, and enrolment in
 * a sheet (the sketch's `sakerhet` section). "Stäng av" is not in the menu for an account that
 * requires a factor, because offering it would be the interface arranging its own lockout.
 */
export function FactorPanel() {
  const queryClient = useQueryClient();
  const gate = useMfaGate();

  const factor = useQuery({
    queryKey: factorsKey,
    queryFn: verifiedFactorId
  });

  const [enrollment, setEnrollment] = useState<EnrollmentStart | null>(null);
  const [code, setCode] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<"enrolled" | "removed" | null>(null);

  const required = gate.data?.adminRequired === true;
  const current = factor.data ?? null;

  const begin = useMutation({
    mutationFn: startEnrollment,
    onSuccess: (started) => {
      setEnrollment(started);
      setCode("");
      setFailure(null);
      setDone(null);
    },
    onError: () => setFailure(ENROLLMENT_FAILURE_TEXT.unavailable)
  });

  async function cancel() {
    setEnrollment(null);
    setCode("");
    setFailure(null);
    await clearUnverifiedFactors();
    await queryClient.invalidateQueries({ queryKey: factorsKey });
  }

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!enrollment) {
      return;
    }

    setFailure(null);
    const result = await confirmEnrollment(enrollment.factorId, code, current);

    if (!result.ok) {
      setFailure(ENROLLMENT_FAILURE_TEXT[result.reason]);
      setCode("");
      return;
    }

    setEnrollment(null);
    setCode("");
    setDone("enrolled");

    // `current` is the factor this one replaced, or null on a first enrolment. The two are different
    // events to a reader: one says a second factor now exists, the other says the one they had was
    // swapped for a different app.
    void recordSecurityEvent(current ? "mfa_replaced" : "mfa_enabled");

    await queryClient.invalidateQueries({ queryKey: factorsKey });
    // `verify` issues a new token, so the gate's own key changes with it. Invalidated anyway,
    // because this panel must not be the one place still showing the previous answer.
    await queryClient.invalidateQueries({ queryKey: ["mfa-gate"] });
  }

  const remove = useMutation({
    mutationFn: async () => {
      if (current) {
        await removeFactor(current);
      }
    },
    onSuccess: async () => {
      setDone("removed");
      void recordSecurityEvent("mfa_disabled");
      await queryClient.invalidateQueries({ queryKey: factorsKey });
      await queryClient.invalidateQueries({ queryKey: ["mfa-gate"] });
    },
    onError: () => setFailure(ENROLLMENT_FAILURE_TEXT.unavailable)
  });

  if (factor.isLoading || gate.isLoading) {
    return (
      <StatePanel title="Tvåfaktorsinloggning" titleId="factor-title">
        <BoxState kind="loading" title="Hämtar tvåfaktorsinloggning" compact />
      </StatePanel>
    );
  }

  if (factor.isError) {
    return (
      <StatePanel title="Tvåfaktorsinloggning" titleId="factor-title">
        <BoxState
          kind="error"
          title="Vi kunde inte läsa din tvåfaktorsinloggning"
          body="Ingenting har ändrats. Försök igen."
          onRetry={() => void factor.refetch()}
          compact
        />
      </StatePanel>
    );
  }

  return (
    <>
      <Panel
        title="Tvåfaktorsinloggning"
        titleId="factor-title"
        hint={required ? FACTOR_REQUIRED_HINT : FACTOR_HINT}
      >
        <div className="k-set-lines">
          <div className="k-set-line">
            <span data-testid="factor-status">{current ? "Påslagen" : "Inte påslagen"}</span>
            {current ? (
              <RowMenu
                label="Ändra tvåfaktorsinloggning"
                testId="factor-menu"
                items={[
                  {
                    label: "Byt till en ny telefon",
                    icon: <ArrowsClockwise size={16} aria-hidden="true" />,
                    disabled: begin.isPending,
                    onSelect: () => begin.mutate()
                  },
                  ...(required
                    ? []
                    : [
                        "separator" as const,
                        {
                          label: "Stäng av",
                          icon: <X size={16} aria-hidden="true" />,
                          danger: true,
                          disabled: remove.isPending,
                          onSelect: () => remove.mutate()
                        }
                      ])
                ]}
              />
            ) : (
              <button
                type="button"
                className="button"
                disabled={begin.isPending}
                onClick={() => begin.mutate()}
              >
                Slå på tvåfaktorsinloggning
              </button>
            )}
          </div>

          {failure && !enrollment ? (
            <p className="form-error" role="alert" data-testid="factor-error">
              {failure}
            </p>
          ) : null}

          {done === "enrolled" ? (
            <p className="form-success" role="status" data-testid="factor-enrolled">
              Klart. Nästa inloggning frågar efter en kod.
            </p>
          ) : null}

          {done === "removed" ? (
            <p className="form-success" role="status" data-testid="factor-removed">
              Tvåfaktorsinloggningen är avstängd.
            </p>
          ) : null}

          {/*
            Said here rather than left to be discovered. Somebody who has lost the authenticator never
            reaches this screen: an `aal1` session holding a verified factor is sent to `/verify`
            and stays there. A button here would be no help to them, so the line points at the one
            route that is.
          */}
          {current ? (
            <p className="line-hint" data-testid="factor-lost-phone">
              Har du tappat telefonen och kan inte logga in? Hör av dig till Petrovic Systems, så återställer vi
              faktorn åt dig.
            </p>
          ) : null}
        </div>
      </Panel>

      <Sheet
        open={enrollment !== null}
        onOpenChange={(open) => {
          if (!open) {
            void cancel();
          }
        }}
        title={current ? "Byt till en ny telefon" : "Slå på tvåfaktorsinloggning"}
        testId="factor-sheet"
      >
        {enrollment ? (
          <form onSubmit={confirm} className="k-form">
            <p>
              Skanna koden i din autentiseringsapp, eller skriv in nyckeln under den. Ange sedan koden
              appen visar.
            </p>

            {/*
              The image is decorative because the same value is printed as text below it and is
              selectable. A screen reader that announced a QR code would be announcing a picture of
              something it is already being given.
            */}
            <img className="factor-qr" src={enrollment.qrCode} alt="" />

            <p className="factor-secret">
              <span className="visually-hidden">Nyckel att skriva in manuellt</span>
              <code data-testid="factor-secret">{enrollment.secret}</code>
            </p>

            <div className="field">
              <label htmlFor="factor-code">Kod från appen</label>
              <input
                id="factor-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>

            {failure ? (
              <p className="form-error" role="alert" data-testid="factor-error">
                {failure}
              </p>
            ) : null}

            <div className="form-actions">
              <button type="button" className="button is-quiet" onClick={() => void cancel()}>
                <span>Avbryt</span>
              </button>
              <button type="submit" className="button is-primary" disabled={code.trim().length < 6}>
                <span>Bekräfta</span>
              </button>
            </div>
          </form>
        ) : null}
      </Sheet>
    </>
  );
}
